import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { geoNaturalEarth1, geoPath, geoGraticule10 } from "d3-geo";
import countries from "../data/countries";
import ISO from "../data/iso";
import { CONT_VIEWS } from "../data/constants";
import topoFeature from "../utils/topoFeature";

const TOPO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const W = 960, H = 540;

const C_ACTIVE   = "#3A6B45";
const C_HOVER    = "#4E8C5C";
const C_DIM      = "#8FAB8F";
const C_ANSWERED = "#7B2020";   // maroon for correctly answered
const C_FLASH_RED   = "#DC2626";
const C_FLASH_WHITE = "#F0F0F0";

export default function QuizMap({ targetCode, flashCode, flashColor, onCountryClick, cont, answeredCodes }) {
  const svgRef = useRef(null);
  const [geo, setGeo] = useState(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const [hoveredCode, setHoveredCode] = useState(null);
  const dragRef = useRef(null);

  const contSet = useMemo(() => {
    const s = new Set();
    countries.forEach(c => {
      if (cont === "All" || c.continent === cont) s.add(c.code);
    });
    return s;
  }, [cont]);

  useEffect(() => {
    fetch(TOPO_URL)
      .then(r => r.json())
      .then(topo => setGeo(topoFeature(topo, topo.objects.countries)))
      .catch(() => setGeo("error"));
  }, []);

  useEffect(() => {
    const v = CONT_VIEWS[cont] || CONT_VIEWS["All"];
    setTransform({ x: 480 - v.x * v.k, y: 270 - v.y * v.k, k: v.k });
  }, [cont]);

  const proj    = useMemo(() => geoNaturalEarth1().scale(210).translate([W / 2, H / 2 - 20]), []);
  const pathGen = useMemo(() => geoPath(proj), [proj]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * W;
    const my = ((e.clientY - rect.top) / rect.height) * H;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setTransform(t => {
      const nk = Math.max(0.8, Math.min(12, t.k * factor));
      return { x: mx - (mx - t.x) * (nk / t.k), y: my - (my - t.y) * (nk / t.k), k: nk };
    });
  }, []);

  const handleDown = useCallback((e) => {
    if (e.button !== 0) return;
    const svg = svgRef.current;
    if (!svg) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, tx: transform.x, ty: transform.y };
    const onMove = ev => {
      if (!dragRef.current) return;
      const rect = svg.getBoundingClientRect();
      const s = W / rect.width;
      setTransform(t => ({
        ...t,
        x: dragRef.current.tx + (ev.clientX - dragRef.current.sx) * s,
        y: dragRef.current.ty + (ev.clientY - dragRef.current.sy) * s,
      }));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [transform.x, transform.y]);

  const zoomIn    = () => setTransform(t => { const nk = Math.min(12, t.k * 1.4); return { x: 480 - (480 - t.x) * (nk / t.k), y: 270 - (270 - t.y) * (nk / t.k), k: nk }; });
  const zoomOut   = () => setTransform(t => { const nk = Math.max(0.8, t.k / 1.4); return { x: 480 - (480 - t.x) * (nk / t.k), y: 270 - (270 - t.y) * (nk / t.k), k: nk }; });
  const zoomReset = () => { const v = CONT_VIEWS[cont] || CONT_VIEWS["All"]; setTransform({ x: 480 - v.x * v.k, y: 270 - v.y * v.k, k: v.k }); };

  if (geo === "error") return <p style={{ padding: 20 }}>Could not load map data. Please refresh.</p>;
  if (!geo) return <div className="qz-loading">Loading map…</div>;

  const tf = `translate(${transform.x},${transform.y}) scale(${transform.k})`;
  const sw = Math.max(0.2, 0.5 / transform.k);

  return (
    <div className="qz-map">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "100%", display: "block", background: "#B8D4E8" }}
        onWheel={handleWheel}
        onMouseDown={handleDown}
      >
        <g style={{ transition: dragRef.current ? "none" : "transform .5s ease" }}>
          <g transform={tf}>
            <path
              d={pathGen(geoGraticule10())}
              fill="none"
              stroke="rgba(255,255,255,.1)"
              strokeWidth={0.25 / transform.k}
            />
            {geo.features.map((f, i) => {
              const code     = ISO[f.id] || ISO[String(f.id)];
              const inCont   = code ? contSet.has(code) : false;
              const answered = code ? answeredCodes.has(code) : false;
              const isFlash  = code === flashCode;
              const hovered  = code === hoveredCode && inCont && !answered && !isFlash;

              let fill;
              if (isFlash)   fill = flashColor === "red" ? C_FLASH_RED : C_FLASH_WHITE;
              else if (answered) fill = C_ANSWERED;
              else if (hovered)  fill = C_HOVER;
              else if (inCont)   fill = C_ACTIVE;
              else               fill = C_DIM;

              const d = pathGen(f);
              if (!d) return null;
              return (
                <path
                  key={i}
                  d={d}
                  fill={fill}
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth={sw}
                  style={{ cursor: inCont && !answered ? "pointer" : "default" }}
                  onMouseEnter={() => { if (inCont && !answered) setHoveredCode(code); }}
                  onMouseLeave={() => setHoveredCode(null)}
                  onClick={() => { if (inCont && !answered && code) onCountryClick(code); }}
                />
              );
            })}
          </g>
        </g>
      </svg>

      <div className="zoom-controls">
        <button className="zoom-btn" onClick={zoomIn}>+</button>
        <button className="zoom-btn" onClick={zoomOut}>−</button>
        <button className="zoom-btn" onClick={zoomReset} style={{ fontSize: 13 }}>↻</button>
      </div>
      <div className="zoom-hint">Scroll to zoom · Drag to pan</div>
    </div>
  );
}
